import { DateTime, Duration } from 'luxon'

export class Rules {
  static required(value) {
    return (value !== null && value !== undefined && String(value).trim() !== '') || 'Required.'
  }

  static requiredWithMessage(message, value) {
    return (value !== null && value !== undefined && String(value).trim() !== '') || message || 'Required.'
  }

  //  String validation
  static maxLength(limit, value) {
    return !value || value.length <= limit || `${value.length - limit} characters over maximum length of ${limit}.`
  }

  static minLength(limit, value) {
    return !value || value.length >= limit || `${limit - value.length} characters under minimal length of ${limit}.`
  }

  static length(limit, value) {
    return !value || value.length === limit || `Please enter a string with a length of ${limit}.`
  }

  //  Number validation
  static isInteger(value) {
    if (typeof value === 'string') {
      value = value.replace(/,/g, '')
    }
    return value === undefined || Number.isInteger(Number(value)) || 'Please enter an integer.'
  }

  static isNumeric(value) {
    if (typeof value === 'string') {
      value = value.replace(/,/g, '')
    }
    return (
      value === undefined || value === null || String(value).trim() === '' || !isNaN(value) || 'Please enter a number.'
    )
  }

  static numberInRange(min, max, value) {
    if (typeof value === 'string') {
      value = value.replace(/,/g, '')
    }
    return (
      value === null ||
      value === undefined ||
      String(value).trim() === '' ||
      isNaN(value) ||
      (value >= min && value <= max) ||
      `Please enter a number between ${min} and ${max}.`
    )
  }

  static minNumber(min, value) {
    if (typeof value === 'string') {
      value = value.replace(/,/g, '')
    }
    return (
      value === null ||
      value === undefined ||
      String(value).trim() === '' ||
      isNaN(value) ||
      value >= min ||
      `Please enter a number greater than or equal to ${min}.`
    )
  }

  static isNegative(value) {
    if (typeof value === 'string') {
      value = value.replace(/,/g, '')
    }
    return value === null || value === undefined || isNaN(value) || value <= 0 || 'Please enter a negative number.'
  }

  static isPositive(value) {
    if (typeof value === 'string') {
      value = value.replace(/,/g, '')
    }
    return value === null || value === undefined || isNaN(value) || value >= 0 || 'Please enter a positive number.'
  }

  //  Misc
  static validIdentifier(value) {
    return (
      !/[^a-zA-Z0-9_-]+/.test(value) ||
      'Please enter a valid identifier. Characters "A-Z", "0-9", "_", and "-" are allowed.'
    )
  }

  static validEmail(value) {
    return !value || /.+@.+/.test(value) || 'Please enter a valid e-mail address.'
  }

  static dateAfter(start, valueFormat, message, value) {
    return (
      !value ||
      !start ||
      DateTime.fromFormat(value, valueFormat, { zone: 'utc' }).set({ hour: 0, minute: 0, second: 0, milliseconds: 0 }) >
        DateTime.fromISO(start, { zone: 'utc' }).set({ hour: 0, minute: 0, second: 0, milliseconds: 0 }) ||
      message ||
      'Please choose a later date.'
    )
  }

  static dateBefore(end, valueFormat, message, value) {
    return (
      !value ||
      !end ||
      DateTime.fromFormat(value, valueFormat, { zone: 'utc' }).set({
        hour: 0,
        minute: 0,
        second: 0,
        milliseconds: 0
      }) <= DateTime.fromISO(end, { zone: 'utc' }).set({ hour: 0, minute: 0, second: 0, milliseconds: 0 }) ||
      message ||
      'Please choose a later date.'
    )
  }

  static isUnique({ conflictValue, conflictMessage }, message, value) {
    return (
      !value ||
      !conflictValue ||
      value !== conflictValue ||
      conflictMessage ||
      message ||
      'Please enter a unique value.'
    )
  }

  static isDuration(value) {
    if (value === null) return true
    if (value === undefined) return true
    const duration = Duration.fromISO(value)
    if (duration.isValid) return true
    return 'Please enter a duration.'
  }

  static minDuration(min, value) {
    if (value === null) return true
    if (value === undefined) return true
    const minDuration = Duration.fromISO(min)
    const duration = Duration.fromISO(value)
    if (!minDuration.isValid) throw new Error('min value must be a valid ISO period')
    if (!duration.isValid) return true
    if (duration.valueOf() >= minDuration.valueOf()) return true
    return `Please enter a duration greater than or equal to ${minDuration.toHuman()}.`
  }

  static durationInRange(min, max, value) {
    if (value === null) return true
    if (value === undefined) return true
    const minDuration = Duration.fromISO(min)
    const maxDuration = Duration.fromISO(max)
    const duration = Duration.fromISO(value)
    if (!minDuration.isValid) throw new Error('min value must be a valid ISO period')
    if (!maxDuration.isValid) throw new Error('max value must be a valid ISO period')
    if (!duration.isValid) return true
    if (duration.valueOf() >= minDuration.valueOf() && duration.valueOf() <= maxDuration.valueOf()) return true
    return `Please enter a duration between ${minDuration.toHuman()} and ${maxDuration.toHuman()}.`
  }

  static durationValueInRange(min, max, value) {
    if (value === null) return true
    if (value === undefined) return true
    if (!Duration.fromISO(value).isValid) return true
    const numericMatch = value.match(/\d+/)
    if (!numericMatch) return true
    const numericValue = parseInt(numericMatch[0], 10)
    return Rules.numberInRange(min, max, numericValue)
  }

  static minDurationValue(min, value) {
    if (value === null) return true
    if (value === undefined) return true
    if (!Duration.fromISO(value).isValid) return true
    const numericMatch = value.match(/\d+/)
    if (!numericMatch) return true
    const numericValue = parseInt(numericMatch[0], 10)
    return Rules.minNumber(min, numericValue)
  }
}

export default { Rules }
